import { type ReactNode, useEffect, useReducer, useRef } from 'react';
import { useGameStore } from '../../../store/gameStore';
import { ScaleToFit } from '../ScaleToFit';
import { useGameLoop } from '../../../hooks/useGameLoop';
import { useKeyboard } from '../../../hooks/useKeyboard';
import { playEat, playGolden } from '../../../utils/sounds';

const ROWS = 15;
const COLS = 20;
const CELL = 24;
const BASE_TICK = 10; // frames per step at start
const MIN_TICK = 4;   // fastest possible (every 4 frames)

interface Pos {
  row: number;
  col: number;
}

interface Food extends Pos {
  isGold: boolean;
}

function spawnFood(snake: Pos[], isGold: boolean): Food {
  const occupied = new Set(snake.map((s) => `${s.row},${s.col}`));
  const free: Pos[] = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      if (!occupied.has(`${r},${c}`)) free.push({ row: r, col: c });
    }
  }
  const pos = free[Math.floor(Math.random() * free.length)] ?? { row: 5, col: 10 };
  return { ...pos, isGold };
}

interface Props {
  active: boolean;
}

export function SnakeGame({ active }: Props) {
  const diveIn = useGameStore((s) => s.diveIn);
  const addScore = useGameStore((s) => s.addScore);
  const gameOver = useGameStore((s) => s.gameOver);
  const phase = useGameStore((s) => s.phase);
  const runCount = useGameStore((s) => s.runCount);
  // Each 2 runs, snake starts 1 tick faster (capped at BASE_TICK - 3)
  const startingTick = useRef(Math.max(MIN_TICK + 1, BASE_TICK - Math.min(3, Math.floor((runCount - 1) / 2))));
  const keys = useKeyboard();
  const [, rerender] = useReducer((x: number) => x + 1, 0);

  const snakeRef = useRef<Pos[]>([
    { row: 7, col: 12 },
    { row: 7, col: 11 },
    { row: 7, col: 10 },
  ]);
  const dirRef = useRef<Pos>({ row: 0, col: 1 });
  const nextDirRef = useRef<Pos>({ row: 0, col: 1 });
  const foodRef = useRef<Food | null>(null);
  const tickRef = useRef(0);
  const foodsEatenRef = useRef(0);
  const deadRef = useRef(false);
  const divingRef = useRef(false);

  useEffect(() => {
    if (active) divingRef.current = false;
  }, [active]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    foodRef.current = spawnFood(snakeRef.current, false);
    rerender();
  }, []);

  useGameLoop(
    () => {
      if (deadRef.current || divingRef.current) return;

      // Queue direction changes
      const { row: dr, col: dc } = dirRef.current;
      if ((keys.has('ArrowUp') || keys.has('KeyW')) && dr !== 1) {
        nextDirRef.current = { row: -1, col: 0 };
      } else if ((keys.has('ArrowDown') || keys.has('KeyS')) && dr !== -1) {
        nextDirRef.current = { row: 1, col: 0 };
      } else if ((keys.has('ArrowLeft') || keys.has('KeyA')) && dc !== 1) {
        nextDirRef.current = { row: 0, col: -1 };
      } else if ((keys.has('ArrowRight') || keys.has('KeyD')) && dc !== -1) {
        nextDirRef.current = { row: 0, col: 1 };
      }

      tickRef.current++;
      const tickRate = Math.max(MIN_TICK, startingTick.current - Math.floor((snakeRef.current.length - 3) / 4));
      if (tickRef.current < tickRate) return;
      tickRef.current = 0;

      dirRef.current = nextDirRef.current;
      const head = snakeRef.current[0];
      if (!head) return;

      const newHead: Pos = {
        row: head.row + dirRef.current.row,
        col: head.col + dirRef.current.col,
      };

      // Wall collision
      if (newHead.row < 0 || newHead.row >= ROWS || newHead.col < 0 || newHead.col >= COLS) {
        deadRef.current = true;
        gameOver();
        return;
      }

      // Self collision
      if (snakeRef.current.some((s) => s.row === newHead.row && s.col === newHead.col)) {
        deadRef.current = true;
        gameOver();
        return;
      }

      const food = foodRef.current;
      const ateFood = food !== null && newHead.row === food.row && newHead.col === food.col;

      if (ateFood && food !== null) {
        if (food.isGold) {
          addScore(100);
          snakeRef.current = [newHead, ...snakeRef.current];
          foodRef.current = null;
          divingRef.current = true;
          playGolden();
          diveIn();
          rerender();
          return;
        } else {
          addScore(10);
          playEat();
          foodsEatenRef.current += 1;
          const nextIsGold = foodsEatenRef.current % 5 === 4;
          foodRef.current = spawnFood([newHead, ...snakeRef.current], nextIsGold);
          snakeRef.current = [newHead, ...snakeRef.current]; // grow
        }
      } else {
        snakeRef.current = [newHead, ...snakeRef.current.slice(0, -1)]; // move
      }

      rerender();
    },
    active && phase === 'playing',
  );

  const snake = snakeRef.current;
  const food = foodRef.current;
  const snakeSet = new Map<string, number>();
  snake.forEach((s, i) => snakeSet.set(`${s.row},${s.col}`, i));

  const cells: ReactNode[] = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const key = `${r},${c}`;
      const snakeIdx = snakeSet.get(key);
      const isFood = food !== null && food.row === r && food.col === c;

      let bg = 'bg-gray-900';
      if (snakeIdx === 0) bg = 'bg-green-400';
      else if (snakeIdx !== undefined) bg = 'bg-green-700';
      else if (isFood && food?.isGold) bg = 'bg-yellow-400';
      else if (isFood) bg = 'bg-red-500';

      cells.push(
        <div
          key={key}
          style={{ width: CELL, height: CELL }}
          className={`${bg} ${isFood && food?.isGold ? 'animate-pulse' : ''}`}
        />,
      );
    }
  }

  return (
    <div className="flex flex-col items-center gap-3 p-4">
      <div className="text-xs text-gray-400 font-mono flex flex-wrap gap-4 justify-center">
        <span>Snake length: {snake.length}</span>
        <span className={Math.max(MIN_TICK, BASE_TICK - Math.floor((snake.length - 3) / 4)) === MIN_TICK ? 'text-red-400 animate-pulse' : 'text-gray-500'}>
          Speed: {BASE_TICK + 1 - Math.max(MIN_TICK, BASE_TICK - Math.floor((snake.length - 3) / 4))}/{BASE_TICK + 1 - MIN_TICK}
          {Math.max(MIN_TICK, BASE_TICK - Math.floor((snake.length - 3) / 4)) === MIN_TICK && ' (MAX)'}
        </span>
        <span className="text-yellow-400">Gold food every 5th eat → dive!</span>
      </div>
      <ScaleToFit nativeWidth={COLS * CELL} nativeHeight={ROWS * CELL}>
        <div
          className="border border-gray-700 rounded overflow-hidden"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${COLS}, ${CELL}px)`,
            width: COLS * CELL,
          }}
        >
          {cells}
        </div>
      </ScaleToFit>
    </div>
  );
}
